import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { PlayCircle, X } from "lucide-react";

function isLikelyVideoUrl(url = "") {
  return /\.(mp4|webm|ogg|mov)(\?|#|$)/i.test(String(url));
}

export function getTemplateVideoUrl(template) {
  if (template?.preview_video_url) {
    return template.preview_video_url;
  }

  return isLikelyVideoUrl(template?.background_image) ? template.background_image : "";
}

export function TemplateAutoVideo({
  template,
  className = "",
  mediaClassName = "",
  fallbackClassName = "",
}) {
  const videoUrl = getTemplateVideoUrl(template);
  const imageUrl = template?.thumbnail_image || template?.background_image;

  return (
    <div className={className}>
      {videoUrl ? (
        <video
          src={videoUrl}
          poster={imageUrl || undefined}
          muted
          autoPlay
          loop
          playsInline
          preload="metadata"
          className={mediaClassName}
        />
      ) : imageUrl ? (
        <img src={imageUrl} alt={template?.name || "Invitation template"} className={mediaClassName} />
      ) : (
        <div className={fallbackClassName}>No preview available</div>
      )}
    </div>
  );
}

export function TemplateVideoModal({ template, isOpen, onClose, onUseTemplate }) {
  const videoUrl = getTemplateVideoUrl(template);
  const imageUrl = template?.thumbnail_image || template?.background_image;

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const modal = (
    <div className="fixed inset-0 z-[100] bg-black/85 px-4 py-5 backdrop-blur-sm md:px-8">
      <div className="mx-auto flex h-full max-w-6xl flex-col">
        <div className="mb-4 flex items-center justify-between gap-4 text-white">
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-white/60">
              Template video preview
            </p>
            <h2 className="mt-1 truncate text-xl font-black md:text-2xl">
              {template?.name || "Invitation Film"}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
            aria-label="Close video preview"
          >
            <X size={22} />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 items-center justify-center rounded-[28px] bg-black shadow-2xl">
          {videoUrl ? (
            <video
              src={videoUrl}
              poster={imageUrl || undefined}
              controls
              autoPlay
              playsInline
              className="max-h-full w-full rounded-[28px] object-contain"
            />
          ) : imageUrl ? (
            <img
              src={imageUrl}
              alt={template?.name || "Invitation template preview"}
              className="max-h-full w-full rounded-[28px] object-contain"
            />
          ) : (
            <div className="flex h-[60vh] w-full items-center justify-center rounded-[28px] bg-[#161116] text-white/70">
              No video preview available yet.
            </div>
          )}
        </div>

        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-white/65">
            Watch the pacing and motion style here, then use the template when it feels right.
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-white/20 px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-white/10"
            >
              Close
            </button>
            {onUseTemplate ? (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onUseTemplate();
                }}
                className="inline-flex items-center gap-2 rounded-full bg-[#ff0099] px-5 py-3 text-sm font-bold text-white shadow-lg shadow-[#ff0099]/25 transition-colors hover:bg-white hover:text-black"
              >
                <PlayCircle size={18} />
                Use Template
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
